import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MagnifyingGlassIcon, PlusIcon, UsersIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import EmptyState from "../../components/EmptyState";
import SkeletonRows from "../../components/SkeletonRows";
import {
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  TextArea,
  TextInput,
} from "../../components/crm/Form";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const WRITE_ROLES = ["owner", "ops"];

export default function CustomersList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [search, setSearch] = useSearchParams();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async (qVal = "") => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/customers/", {
        params: qVal ? { q: qVal } : {},
      });
      setList(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Phase 15 — auto-open create modal when navigated to with ?new=1.
  useEffect(() => {
    if (search.get("new") === "1" && canWrite) {
      setOpen(true);
      const next = new URLSearchParams(search);
      next.delete("new");
      setSearch(next, { replace: true });
    }
  }, [search, canWrite, setSearch]);

  const submitSearch = (e) => {
    e.preventDefault();
    load(q);
  };

  return (
    <Layout title="Customers" subtitle="CRM">
      <EntityHeader
        overline="people"
        title="Customers"
        subtitle="Account records for everyone Zion Solar serves."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form onSubmit={submitSearch} className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlassIcon
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            data-testid="customers-search"
            className="w-full rounded-sm border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
          />
        </form>
        {canWrite && (
          <PrimaryButton
            onClick={() => setOpen(true)}
            data-testid="customers-create-button"
          >
            <PlusIcon size={14} weight="bold" /> New Customer
          </PrimaryButton>
        )}
      </div>

      <ErrorBanner message={err} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Name</th>
              <th className="text-left px-5 py-3 font-bold">Email</th>
              <th className="text-left px-5 py-3 font-bold">Phone</th>
              <th className="text-left px-5 py-3 font-bold">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && list.length === 0 && <SkeletonRows count={5} cols={4} />}
            {!loading && list.length === 0 && (
              <EmptyState
                inTable
                colSpan={4}
                icon={UsersIcon}
                title={q ? "No matching customers" : "No customers yet"}
                description={
                  q
                    ? "Try a different search term — we matched on name, email, and phone."
                    : "Add your first customer to start tracking their properties, systems, and jobs."
                }
                cta={
                  !q && canWrite
                    ? {
                        label: "Add Customer",
                        onClick: () => setOpen(true),
                        testId: "customers-empty-cta",
                      }
                    : null
                }
                testId="customers-empty"
              />
            )}
            {list.map((c) => (
              <tr
                key={c.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/customers/${c.id}`)}
                data-testid={`customer-row-${c.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900">
                  {c.name}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {c.email || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {c.phone || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-600 tabular">
                  {new Date(c.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canWrite && (
        <CreateCustomerModal
          open={open}
          onClose={() => setOpen(false)}
          onCreated={(c) => {
            setOpen(false);
            nav(`/customers/${c.id}`);
          }}
        />
      )}
    </Layout>
  );
}

export function CreateCustomerModal({ open, onClose, onCreated, initial }) {
  const empty = useMemo(
    () => ({ name: "", email: "", phone: "", notes: "" }),
    []
  );
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...empty, ...initial } : empty);
      setErr("");
    }
  }, [open, initial, empty]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = { name: form.name };
      if (form.email) payload.email = form.email;
      if (form.phone) payload.phone = form.phone;
      if (form.notes) payload.notes = form.notes;
      const { data } = await api.post("/customers/", payload);
      onCreated(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Customer"
      subtitle="add to crm"
      data-testid="customer-create-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" required>
          <TextInput
            required
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            data-testid="customer-name-input"
            maxLength={200}
          />
        </Field>
        <Field label="Email">
          <TextInput
            type="email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            data-testid="customer-email-input"
          />
        </Field>
        <Field label="Phone">
          <TextInput
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            data-testid="customer-phone-input"
          />
        </Field>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            data-testid="customer-notes-input"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="customer-submit"
          >
            {busy ? "Creating…" : "Create Customer"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
